import { can, type ExportQuery, excelMoneyFormat, type MeDto, shortDate } from '@hudhuria/shared';
import { buildXlsx, XLSX_MIME } from '@hudhuria/shared/xlsx';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { exportQuery, useCan, useCenter, useCenterId, useMe } from '~/api/queries';
import { DownloadIcon } from '~/components/icons';
import {
  Overlay,
  OverlayFooter,
  OverlayHeader,
  PrimaryButton,
  SectionLabel,
  SegmentedControl,
  Spinner,
} from '~/components/ui';
import { saveFile } from '~/lib/files';
import { useBack } from '~/lib/nav';
import { toast } from '~/lib/toast';
import { useOnline } from '~/offline/online';

export const Route = createFileRoute('/_app/export')({
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData<MeDto>(['me']);
    if (!can(me?.staff.role, 'export')) throw redirect({ to: '/hub' });
  },
  component: Export,
});

const TYPES: { value: ExportQuery['type']; label: string; sub: string }[] = [
  { value: 'attendance', label: 'Attendance', sub: 'Check-ins' },
  { value: 'payments', label: 'Payments', sub: 'Fees & receipts' },
  { value: 'all', label: 'Full report', sub: '4 sheets' },
];

const PAY_FG: Record<string, string> = { Paid: '#047857', 'Pay later': '#b45309' };

/** Excel export, built on the device from the server's report data. */
function Export() {
  const back = useBack('/me');
  const me = useMe().data;
  const center = useCenter();
  const centerId = useCenterId();
  const online = useOnline();
  const allCenters = useCan('allCenters');
  const [q, setQ] = useState<ExportQuery>({ type: 'attendance', range: 'today', scope: 'center' });
  const report = useQuery({ ...exportQuery(centerId, q), enabled: !!centerId && online });
  const [saving, setSaving] = useState(false);
  const currency = me?.institution.currency ?? 'KES';
  const tz = me?.institution.timezone ?? 'Africa/Nairobi';

  const data = report.data;
  const rows = data
    ? q.type === 'payments'
      ? data.counts.payments
      : q.type === 'all'
        ? data.counts.attendance + data.counts.payments + data.counts.students
        : data.counts.attendance
    : 0;
  const listed = data ? (q.type === 'payments' ? data.counts.payments : data.counts.attendance) : 0;

  const download = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const bytes = buildXlsx(data.sheets, excelMoneyFormat(currency));
      await saveFile(data.filename, bytes, XLSX_MIME, 'Hudhuria report');
      toast(`Exported ${rows} rows to Excel`);
    } catch {
      toast('Export failed — try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay>
      <OverlayHeader
        back="Back"
        onBack={back}
        title="Export report"
        subtitle="Excel workbook (.xlsx) · opens in Excel, Google Sheets or Numbers"
      />
      <div className="flex flex-1 flex-col gap-[18px] px-5 pt-1.5 pb-4 scroll-y">
        <div>
          <SectionLabel className="mb-[9px]">Report</SectionLabel>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map((t) => {
              const on = q.type === t.value;
              return (
                <button
                  type="button"
                  key={t.value}
                  onClick={() => setQ({ ...q, type: t.value })}
                  className="cursor-pointer rounded-[13px] px-[10px] py-3 text-left"
                  style={{
                    border: `1.5px solid ${on ? '#0369a1' : '#e8ecf3'}`,
                    background: on ? '#e6f4fc' : '#fff',
                  }}
                >
                  <div className="text-[13.5px] font-semibold" style={{ color: on ? '#0369a1' : '#0f172a' }}>
                    {t.label}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400">{t.sub}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <SectionLabel className="mb-[9px]">Period</SectionLabel>
          <SegmentedControl
            value={q.range}
            onChange={(range) => setQ({ ...q, range })}
            options={[
              { value: 'today', label: 'Today' },
              { value: 'week', label: '7 days' },
              { value: 'month', label: 'This month' },
            ]}
          />
        </div>
        <div>
          <SectionLabel className="mb-[9px]">Centers</SectionLabel>
          <SegmentedControl
            value={q.scope}
            onChange={(scope) => setQ({ ...q, scope })}
            options={
              allCenters
                ? [
                    { value: 'center', label: center?.name ?? 'This center' },
                    { value: 'all', label: 'All centers' },
                  ]
                : [{ value: 'center', label: center?.name ?? 'This center' }]
            }
          />
        </div>

        <div className="shrink-0 overflow-hidden rounded-2xl border border-line">
          <div className="flex items-center gap-[10px] border-b border-line bg-fill-soft px-[14px] py-3">
            <XlsxFileIcon />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold">{data?.filename ?? 'Preparing…'}</div>
              <div className="mt-px text-[11.5px] text-slate-500">
                {data
                  ? data.sheets.map((s) => s.name).join(' · ')
                  : online
                    ? 'Loading…'
                    : 'Connect to the internet to export'}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-[62px_minmax(0,1fr)_74px] gap-2 border-b border-line-faint px-[14px] py-[9px] text-[10.5px] font-bold tracking-[0.05em] text-slate-400">
            <div>DATE</div>
            <div>STUDENT · PURPOSE</div>
            <div className="text-right">PAYMENT</div>
          </div>
          {report.isFetching && !data && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}
          {data?.preview.map((r, i) => {
            const pay = String(r[10] ?? '');
            return (
              <div
                key={i}
                className="grid grid-cols-[62px_minmax(0,1fr)_74px] items-center gap-2 border-b border-line-faint px-[14px] py-[9px] text-[12px]"
              >
                <div className="font-display text-slate-500">{shortDate(String(r[0]), tz)}</div>
                <div className="min-w-0">
                  <div className="truncate font-semibold">{r[3]}</div>
                  <div className="truncate text-[11px] text-slate-400">
                    {r[8]} · {r[6]}
                  </div>
                </div>
                <div className="text-right font-semibold" style={{ color: PAY_FG[pay] ?? '#94a3b8' }}>
                  {pay === 'Free' ? '—' : pay}
                </div>
              </div>
            );
          })}
          {data && data.preview.length === 0 && (
            <div className="px-[14px] py-[14px] text-[12px] text-slate-400">
              No check-ins in this period yet.
            </div>
          )}
          {data && listed > data.preview.length && (
            <div className="px-[14px] py-[10px] text-[12px] text-slate-500">
              + {listed - data.preview.length} more rows
            </div>
          )}
        </div>
      </div>
      <OverlayFooter>
        <PrimaryButton onClick={() => void download()} disabled={!data || !online} loading={saving}>
          <DownloadIcon />
          Download .xlsx · {rows} rows
        </PrimaryButton>
      </OverlayFooter>
    </Overlay>
  );
}

function XlsxFileIcon() {
  return (
    <svg width="34" height="40" viewBox="0 0 34 40" fill="none" className="shrink-0">
      <path
        d="M6 1.5h17L32.5 11v26a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 37V3A1.5 1.5 0 0 1 6 1.5z"
        fill="#fff"
        stroke="#cbd5e1"
        strokeWidth="1.5"
      />
      <path
        d="M23 1.5v8a1.5 1.5 0 0 0 1.5 1.5h8"
        fill="#f1f5f9"
        stroke="#cbd5e1"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <rect x="10" y="13" width="18" height="12" rx="1.5" fill="#ecfdf3" stroke="#16a34a" strokeWidth="1.2" />
      <path d="M10 17h18M10 21h18M16 13v12" stroke="#16a34a" strokeWidth="1.2" />
      <rect x="0.5" y="27.5" width="22" height="8.5" rx="2" fill="#15803d" />
      <text
        x="11.5"
        y="34"
        textAnchor="middle"
        fontFamily="Space Grotesk, sans-serif"
        fontSize="6.6"
        fontWeight="700"
        fill="#fff"
        letterSpacing=".5"
      >
        XLSX
      </text>
    </svg>
  );
}
